import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import type { ResourceType } from 'types/resource-types';
import type { FilterFieldAttribute, TableColumn } from 'types/views';

import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useLegacyAxiosInstance,
} from './utils';

const URL = 'views';

// Attribute-based bulk view update against the LEGACY endpoint
// (POST /private_api/views). Each table-column entry is a partial TableColumn
// plus the persistence flags the legacy API expects (order, nested-attribute
// id, and `_destroy` for removals).
export type TableColumnAttributes = Partial<TableColumn> & {
  order?: number;
  _destroy?: boolean;
};

// One entry of the bulk `views_attributes` payload. All fields are optional so
// the same hook serves every caller: column updates ({ id, view_type,
// table_columns_attributes }), new-view-with-filters ({ name, view_type,
// filter_fields_attributes }) and in-place filter updates ({ id,
// filter_fields_attributes }).
type ViewAttributes = {
  id?: string;
  name?: string;
  view_type?: string;
  table_columns_attributes?: TableColumnAttributes[];
  filter_fields_attributes?: FilterFieldAttribute[];
};

export type UpdateViewsVariables = {
  views_attributes: ViewAttributes[];
  resourceType: ResourceType;
};

export async function updateViewsFn(
  axiosInstance: Promise<AxiosInstance>,
  { views_attributes }: UpdateViewsVariables,
): Promise<ApiResponse> {
  const instance = await axiosInstance;

  return instance.post(URL, { views_attributes });
}

export function useUpdateViews(
  options: MutationOptions<ApiResponse, ApiError, UpdateViewsVariables> = {},
) {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateViewsVariables>({
    mutationFn: variables => updateViewsFn(axiosInstance, variables),
    onSuccess: (_response, { resourceType }) => {
      client.invalidateQueries({ queryKey: ['views', resourceType] });
    },
    ...options,
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, UpdateViewsVariables, 'updateViews'>(
    'updateViews',
    mutation,
  );
}
